import React, {FC, memo} from "react";
import {Button, Form, Input, InputNumber, Select, Space, Tooltip} from "antd";
import {DeleteOutlined, InfoCircleTwoTone, PlusOutlined} from "@ant-design/icons";
import {blue, red} from "@ant-design/colors";
import {AdditionalItemMode, CourseAdditionalItemInput, useGetAppSettingsQuery} from "../../../generated/graphql";
import MoneyInput from "../../../views/MoneyInput/MoneyInput";
import i18n from "../../../services/i18n";

// Editor für Zusatzposten (Titel + Preis + Modus) — wird im Kurs-Wizard
// (Schritt Kurspreis) und im Kursarten-Editor verwendet. Bewusst controlled
// (value/onChange) statt Formik-gebunden, damit beide Formulare ihn nutzen können.
type AdditionalItemsEditorProps = {
    value: Array<CourseAdditionalItemInput>;
    onChange: (items: Array<CourseAdditionalItemInput>) => void;
    testId?: string;
    style?: React.CSSProperties;
};

/**
 * ZP-16: Gemeinsame Zeilen-Validierung für Kurs UND Kursart — gespiegelt zum
 * Server-Guard (assertAdditionalItemsAllowed): Titel-Pflicht, Preis > 0,
 * ganze Cents, Obergrenze. Liefert die anzeigbare Fehlermeldung oder undefined.
 */
export const getAdditionalItemError = (item: Pick<CourseAdditionalItemInput, "title" | "grossPrice" | "vat">): string | undefined => {
    const tr = i18n.containers.courses.CourseCreate.additionalItems;

    if (item.title.trim().length === 0) {
        return tr.titleRequired();
    }

    const grossPrice = Number(item.grossPrice);

    if (!isFinite(grossPrice) || grossPrice <= 0) {
        return tr.priceRequired();
    }

    const grossPriceInCents = grossPrice * 100;

    if (Math.abs(grossPriceInCents - Math.round(grossPriceInCents)) > 1e-6 || grossPrice > 99999.99) {
        return tr.priceInvalid();
    }

    // Item-MwSt: eigener Satz als Faktor (0 <= vat < 1); null = Kurs-Satz.
    const vat = (item as {vat?: number | null}).vat;

    if (vat !== undefined && vat !== null && (!isFinite(Number(vat)) || Number(vat) < 0 || Number(vat) >= 1)) {
        return tr.vatInvalid();
    }

    return undefined;
};

/**
 * §4.3: Submit-Blocking für alle drei Formulare (Kurs anlegen, Kurs bearbeiten,
 * Kursart) — ungültige Zeilen dürfen das Formular NIE verlassen und NIE still
 * verworfen werden.
 */
export const hasAdditionalItemErrors = (items: Array<Pick<CourseAdditionalItemInput, "title" | "grossPrice" | "vat">> | undefined): boolean =>
    (items ?? []).some((item) => getAdditionalItemError(item) !== undefined);

/** §4.3: Fokus auf die erste fehlerhafte Zeile (Titel-Feld) setzen. */
export const focusFirstAdditionalItemError = (items: Array<Pick<CourseAdditionalItemInput, "title" | "grossPrice" | "vat">> | undefined): void => {
    const firstErrorIndex = (items ?? []).findIndex((item) => getAdditionalItemError(item) !== undefined);

    if (firstErrorIndex < 0) {
        return;
    }

    const input = document.querySelector<HTMLInputElement>(`[data-testid="AdditionalItemTitle-${firstErrorIndex}"]`);

    input?.focus();
    input?.scrollIntoView({block: "center"});
};

const AdditionalItemsEditorComponent: FC<AdditionalItemsEditorProps> = ({value, onChange, testId, style}) => {
    const tr = i18n.containers.courses.CourseCreate.additionalItems;

    // „Verpflichtend für Neukunden" ist nur wählbar, wenn die Neukunden-
    // Erkennung (Settings → App) scharf geschaltet ist — der Server lehnt den
    // Modus sonst ab (assertAdditionalItemsAllowed). Solange die Settings noch
    // laden, bleibt die Option vorsichtshalber gesperrt.
    const {data: settingsData} = useGetAppSettingsQuery();
    const newCustomerDetectionEnabled = settingsData?.settings?.newCustomerDetectionEnabled === true;

    const updateItem = (index: number, patch: Partial<CourseAdditionalItemInput>) => {
        onChange(value.map((item, itemIndex) => (itemIndex === index ? {...item, ...patch} : item)));
    };

    const removeItem = (index: number) => {
        onChange(value.filter((item, itemIndex) => itemIndex !== index));
    };

    const addItem = () => {
        onChange([...value, {title: "", grossPrice: 0, mode: AdditionalItemMode.Optional}]);
    };

    return (
        <div data-testid={testId} style={style}>
            <Form.Item
                style={{
                    border: "1px solid lightGrey",
                    borderRadius: 5,
                    marginBottom: 10,
                    padding: "0 10px",
                }}
                label={
                    <Space size="small">
                        {tr.label()}
                        {/* ZP-21: Tooltip auch per Tastatur erreichbar */}
                        <Tooltip title={tr.tooltip()}>
                            <span tabIndex={0} aria-label={tr.tooltip()}>
                                <InfoCircleTwoTone twoToneColor={blue[5]} />
                            </span>
                        </Tooltip>
                    </Space>
                }
            >
                {value.map((item, index) => {
                    const rowError = getAdditionalItemError(item);
                    const rowName = item.title.trim().length > 0 ? item.title : `${index + 1}`;
                    // §4.6: Fehlertext programmatisch mit den Feldern verknüpfen.
                    const errorId = `additional-item-error-${index}`;
                    const describedBy = rowError ? errorId : undefined;

                    return (
                        // Bestehende Posten haben eine stabile id (ZP-01); nur neue,
                        // noch ungespeicherte Zeilen fallen auf den Index zurück.
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={item.id ?? index} style={{marginBottom: 8}}>
                            <div style={{display: "flex", gap: 8}}>
                                <Input
                                    placeholder={tr.titlePlaceholder()}
                                    aria-label={tr.rowTitleAria({row: rowName})}
                                    aria-describedby={describedBy}
                                    aria-invalid={item.title.trim().length === 0 && rowError ? true : undefined}
                                    value={item.title}
                                    onChange={(event) => updateItem(index, {title: event.target.value})}
                                    style={{flex: "1 1 auto"}}
                                    status={item.title.trim().length === 0 && rowError ? "error" : undefined}
                                    data-testid={`AdditionalItemTitle-${index}`}
                                />
                                <MoneyInput
                                    placeholder={tr.pricePlaceholder()}
                                    aria-label={tr.rowPriceAria({row: rowName})}
                                    aria-describedby={describedBy}
                                    aria-invalid={rowError && item.title.trim().length > 0 ? true : undefined}
                                    value={item.grossPrice}
                                    onChange={(newValue: number | string | null | undefined) => updateItem(index, {grossPrice: Number(newValue ?? 0)})}
                                    style={{width: 90, flex: "0 0 auto"}}
                                />
                                {/* Item-MwSt: eigener Satz in Prozent; leer = Kurs-Satz (Faktor im Datenmodell) */}
                                <InputNumber
                                    placeholder={tr.vatPlaceholder()}
                                    aria-label={tr.rowVatAria({row: rowName})}
                                    aria-describedby={describedBy}
                                    aria-invalid={rowError && item.title.trim().length > 0 ? true : undefined}
                                    value={item.vat !== undefined && item.vat !== null ? Number((Number(item.vat) * 100).toFixed(2)) : undefined}
                                    min={0}
                                    max={99}
                                    onChange={(newValue: number | string | null | undefined) =>
                                        updateItem(index, {vat: newValue === null || newValue === undefined || newValue === "" ? null : Number(newValue) / 100})
                                    }
                                    style={{width: 80, flex: "0 0 auto"}}
                                    addonAfter="%"
                                />
                                <Select<AdditionalItemMode>
                                    value={item.mode}
                                    onChange={(mode) => updateItem(index, {mode})}
                                    style={{width: 220, flex: "0 0 auto"}}
                                    aria-label={tr.rowModeAria({row: rowName})}
                                    data-testid={`AdditionalItemMode-${index}`}
                                >
                                    <Select.Option value={AdditionalItemMode.Optional}>{tr.mode.Optional()}</Select.Option>
                                    <Select.Option value={AdditionalItemMode.Mandatory}>{tr.mode.Mandatory()}</Select.Option>
                                    <Select.Option value={AdditionalItemMode.MandatoryFirstBooking} disabled={!newCustomerDetectionEnabled}>
                                        <Tooltip title={newCustomerDetectionEnabled ? tr.modeFirstBookingHint() : tr.modeFirstBookingDisabledHint()}>
                                            {tr.mode.MandatoryFirstBooking()}
                                        </Tooltip>
                                    </Select.Option>
                                </Select>
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeItem(index)}
                                    aria-label={tr.rowRemoveAria({row: rowName})}
                                    data-testid={`AdditionalItemRemove-${index}`}
                                />
                            </div>
                            {/* Hinweistext: erscheint im Online-Checkout unter dem Posten,
                                nur wenn befüllt (rein deklarativ, nicht preisrelevant). */}
                            <Input
                                placeholder={tr.descriptionPlaceholder()}
                                aria-label={tr.rowDescriptionAria({row: rowName})}
                                value={item.description ?? ""}
                                onChange={(event) => updateItem(index, {description: event.target.value})}
                                maxLength={1000}
                                style={{marginTop: 4}}
                                data-testid={`AdditionalItemDescription-${index}`}
                            />
                            {/* ZP-16: Inline-Fehler statt stillem Submit-Filter */}
                            {rowError && (
                                <div id={errorId} role="alert" style={{color: red[5], fontSize: 12, marginTop: 2}} data-testid={`AdditionalItemError-${index}`}>
                                    {rowError}
                                </div>
                            )}
                        </div>
                    );
                })}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={addItem} style={{marginBottom: 10}} data-testid="AdditionalItemAdd">
                    {tr.addItem()}
                </Button>
            </Form.Item>
        </div>
    );
};

export const AdditionalItemsEditor = memo(AdditionalItemsEditorComponent);
export default AdditionalItemsEditor;
